import React, { useEffect, useState } from 'react';
import AppIcon from '../../components/AppIcon';
import { useNavigate } from 'react-router-dom';
import client from '../../api/client';
import { Save, RotateCcw, Loader2, Check, AlertTriangle, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/use-toast';
import InfoTip from '../../components/InfoTip';
import AiAssistButton from '../../components/AiAssistButton';
import HostSmsAuthorization from '../../components/compliance/HostSmsAuthorization';
import BillingCard from '../../components/billing/BillingCard';
import ConciergeDisclosures from '../../components/compliance/ConciergeDisclosures';
import ConciergeScriptEditor from '../../components/concierge/ConciergeScriptEditor';

const STYLE_TAGS = ['Modern', 'Rustic', 'Classic', 'Boho', 'Garden', 'Beach', 'Minimalist', 'Vintage'];

export default function Settings({ onNavigate }) {
  const [profile, setProfile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [taglineSuggestions, setTaglineSuggestions] = useState([]);
  const [savedAt, setSavedAt] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetting, setResetting] = useState(false);
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await client.get('/profile');
        setProfile(data);
      } catch (e) { /* ignore */ }
    })();
  }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      const { data } = await client.put('/profile', patch);
      setProfile(data);
      setSavedAt(new Date());
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const toggleStyleTag = (tag) => {
    const current = profile?.styleTags || [];
    const next = current.includes(tag) ? current.filter((t) => t !== tag) : [...current, tag];
    setProfile((p) => ({ ...p, styleTags: next }));
    save({ styleTags: next });
  };

  const doReset = async () => {
    setResetting(true);
    try {
      await client.post('/onboarding/reset');
      toast({ title: 'Redoing your setup', description: 'Your existing answers are preserved.' });
      navigate('/onboarding');
    } catch (e) {
      toast({ title: 'Reset failed', description: e?.response?.data?.detail || e.message });
    } finally {
      setResetting(false);
      setConfirmReset(false);
    }
  };

  const handleSignOut = async () => { await logout(); navigate('/'); };

  if (!profile) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#7B2C3B]" /></div>;
  }

  return (
    <div data-testid="settings-page" className="animate-float-up max-w-3xl">
      <div className="flex items-end justify-between mb-8">
        <div>
          <p className="section-eyebrow">Your profile</p>
          <h1 className="font-serif text-4xl text-[#2A2A2A] mt-2 flex items-center gap-3"><AppIcon feature="settings" size="lg" reveal testId="section-icon-settings" />Settings</h1>
        </div>
        <div className="text-xs text-[#9A8A7E] inline-flex items-center gap-1.5">
          {saving ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : savedAt ? <><Check className="w-3.5 h-3.5 text-[#4A7A4A]" /> Saved</> : 'Autosaves on blur'}
        </div>
      </div>

      <section className="soft-card rounded-2xl p-6 bg-white border border-[#EFE3D8] mb-5">
        <h3 className="font-serif text-xl text-[#2A2A2A] mb-4">Wedding basics</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Your name">
            <input data-testid="settings-partner1" value={profile.partner1 || ''} onChange={(e) => setProfile((p) => ({ ...p, partner1: e.target.value }))} onBlur={(e) => save({ partner1: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]" placeholder="Maya" />
          </Field>
          <Field label="Partner's name">
            <input data-testid="settings-partner2" value={profile.partner2 || ''} onChange={(e) => setProfile((p) => ({ ...p, partner2: e.target.value }))} onBlur={(e) => save({ partner2: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]" placeholder="Jordan" />
          </Field>
          <div className="sm:col-span-2">
            <Field label={
              <span className="inline-flex items-center gap-1.5">
                Whose name comes first in messages?
                <InfoTip testId="info-name-order" title="Order used in {coupleNames}">
                  <p>Controls how <code>{'{coupleNames}'}</code> renders across all invitation emails, SMS, RSVP confirmations and reminders — e.g. "Christina and Brent" vs "Brent and Christina".</p>
                </InfoTip>
              </span>
            }>
              <div data-testid="settings-name-order" className="inline-flex rounded-full bg-[#FBF7F2] border border-[#EFE3D8] p-0.5 text-xs">
                <button
                  type="button"
                  data-testid="settings-name-order-p1"
                  onClick={() => { const v='partner1_first'; setProfile((p)=>({...p, nameOrder:v})); save({ nameOrder:v }); }}
                  className={`px-3 py-1 rounded-full transition-all ${(profile.nameOrder || 'partner1_first') === 'partner1_first' ? 'bg-[#7B2C3B] text-white' : 'text-[#3A3A3A]'}`}
                >
                  {profile.partner1 || 'Your name'} first
                </button>
                <button
                  type="button"
                  data-testid="settings-name-order-p2"
                  onClick={() => { const v='partner2_first'; setProfile((p)=>({...p, nameOrder:v})); save({ nameOrder:v }); }}
                  className={`px-3 py-1 rounded-full transition-all ${profile.nameOrder === 'partner2_first' ? 'bg-[#7B2C3B] text-white' : 'text-[#3A3A3A]'}`}
                >
                  {profile.partner2 || "Partner's name"} first
                </button>
              </div>
            </Field>
          </div>
          <Field label="Wedding date">
            <input data-testid="settings-wedding-date" type="date" value={profile.weddingDate || ''} onChange={(e) => setProfile((p) => ({ ...p, weddingDate: e.target.value }))} onBlur={(e) => save({ weddingDate: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]" />
          </Field>
          <Field label="Ceremony start time">
            <input data-testid="settings-wedding-time" value={profile.weddingTime || ''} onChange={(e) => setProfile((p) => ({ ...p, weddingTime: e.target.value }))} onBlur={(e) => save({ weddingTime: e.target.value })} placeholder="4:30 PM" className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]" />
          </Field>
          {/* Timezone — required before the RSVP digest can schedule sends.
              Uses the browser's Intl API to enumerate zones so no static list
              gets stale. Auto-detects on first render if empty. */}
          <Field label="Your timezone (needed for scheduled RSVP digests)">
            <select
              id="timezone"
              data-testid="settings-timezone"
              value={profile.timezone || ''}
              onChange={(e) => { const v = e.target.value; setProfile((p) => ({ ...p, timezone: v })); save({ timezone: v }); }}
              className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]"
            >
              <option value="">— pick one —</option>
              {(typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : ['America/Los_Angeles','America/Denver','America/Chicago','America/New_York','America/Toronto','Europe/London','Europe/Berlin','Europe/Paris','Europe/Amsterdam','Australia/Sydney','Asia/Tokyo','Asia/Singapore','Pacific/Auckland']).map((z) => (
                <option key={z} value={z}>{z}</option>
              ))}
            </select>
            {!profile.timezone && (
              <button
                type="button"
                data-testid="settings-timezone-autodetect"
                className="mt-2 text-xs text-[#7B2C3B] hover:underline"
                onClick={() => {
                  try {
                    const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
                    if (detected) { setProfile((p) => ({ ...p, timezone: detected })); save({ timezone: detected }); }
                  } catch { /* noop */ }
                }}
              >Use my current timezone</button>
            )}
          </Field>
          <div className="sm:col-span-2">
            <Field label={
              <span className="inline-flex items-center gap-1.5">
                Countdown tagline (optional — shown on your dashboard &amp; public site under the days-to-go)
                <InfoTip testId="info-countdown-tagline" title="Personalize the countdown">
                  <p>A short line that shows on both your dashboard ribbon and your public wedding-site countdown, right under the "X days to go" number.</p>
                  <p>Up to 140 characters. Leave blank to use our auto-generated stage messages ("countdown week", "tomorrow!", "today", etc.).</p>
                  <p><strong>Fun examples</strong>:</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li>"Salsa dancing, second wedding, first tacos afterwards."</li>
                    <li>"He said yes to the dress. And the tacos. And you."</li>
                    <li>"Come thirsty, leave family."</li>
                  </ul>
                </InfoTip>
                <AiAssistButton
                  feature="tagline"
                  testId="tagline-ai-suggest"
                  label="Suggest"
                  small
                  context={{ mood: 'warm and playful' }}
                  onResult={(data) => setTaglineSuggestions(data?.items || [])}
                />
              </span>
            }>
              <input
                data-testid="settings-countdown-tagline"
                value={profile.countdownTagline || ''}
                onChange={(e) => setProfile((p) => ({ ...p, countdownTagline: e.target.value }))}
                onBlur={(e) => save({ countdownTagline: e.target.value })}
                placeholder="e.g. Salsa dancing, second wedding, first tacos afterwards."
                maxLength={140}
                className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]"
              />
            </Field>
            <p className="text-[10px] text-[#9A8A7E] mt-1">Leave blank to use our auto-generated stage messages ("countdown week", "tomorrow!", etc.).</p>
            {taglineSuggestions.length > 0 && (
              <div data-testid="tagline-suggestions" className="mt-2 flex flex-wrap gap-1.5">
                {taglineSuggestions.map((t, i) => (
                  <button
                    key={i}
                    data-testid={`tagline-suggestion-${i}`}
                    onClick={() => {
                      setProfile((p) => ({ ...p, countdownTagline: t }));
                      save({ countdownTagline: t });
                      setTaglineSuggestions([]);
                    }}
                    className="text-xs px-2.5 py-1 rounded-full bg-white border border-[#EFE3D8] hover:border-[#7B2C3B] hover:bg-[#F4E4DA] text-left max-w-full truncate"
                    title="Click to use this"
                  >
                    {t}
                  </button>
                ))}
                <button data-testid="tagline-suggestion-clear" onClick={() => setTaglineSuggestions([])} className="text-xs text-[#9A8A7E] px-2 py-1 hover:text-[#7B2C3B]">dismiss</button>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="soft-card rounded-2xl p-6 bg-white border border-[#EFE3D8] mb-5">
        <h3 className="font-serif text-xl text-[#2A2A2A] mb-4">Where</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Venue">
            <input data-testid="settings-venue" value={profile.venue || ''} onChange={(e) => setProfile((p) => ({ ...p, venue: e.target.value }))} onBlur={(e) => save({ venue: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]" placeholder="Beltane Ranch" />
          </Field>
          <Field label="Location (city, region)">
            <input data-testid="settings-location" value={profile.location || ''} onChange={(e) => setProfile((p) => ({ ...p, location: e.target.value }))} onBlur={(e) => save({ location: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]" placeholder="Napa, CA" />
          </Field>
          <div className="sm:col-span-2">
            <Field label={
              <span className="inline-flex items-center gap-1.5">
                Venue full address (linked to Google Maps)
                <InfoTip testId="info-venue-address" title="Turn-by-turn from anywhere">
                  <p>The full mailing address of your venue. Appears in your invitation email right under the venue name, on the RSVP page and on your public wedding site.</p>
                  <p>Guests tap the address and instantly open <strong>Google Maps</strong> with directions from wherever they are.</p>
                  <p><strong>Example</strong>: <em>3801 E Coast Hwy, Corona del Mar, CA 92625</em></p>
                </InfoTip>
              </span>
            }>
              <input data-testid="settings-venue-address" value={profile.venueAddress || ''} onChange={(e) => setProfile((p) => ({ ...p, venueAddress: e.target.value }))} onBlur={(e) => save({ venueAddress: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]" placeholder="3801 E Coast Hwy, Corona del Mar, CA 92625" />
            </Field>
          </div>
        </div>
      </section>

      <section className="soft-card rounded-2xl p-6 bg-white border border-[#EFE3D8] mb-5">
        <h3 className="font-serif text-xl text-[#2A2A2A] mb-4">Guests &amp; budget</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Guest estimate">
            <input data-testid="settings-guest-estimate" type="number" value={profile.guestEstimate ?? ''} onChange={(e) => setProfile((p) => ({ ...p, guestEstimate: e.target.value === '' ? null : Number(e.target.value) }))} onBlur={(e) => save({ guestEstimate: e.target.value === '' ? null : Number(e.target.value) })} className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]" placeholder="120" />
          </Field>
          <Field label="Total budget ($)">
            <input data-testid="settings-budget-total" type="number" value={profile.budgetTotal ?? ''} onChange={(e) => setProfile((p) => ({ ...p, budgetTotal: e.target.value === '' ? null : Number(e.target.value) }))} onBlur={(e) => save({ budgetTotal: e.target.value === '' ? null : Number(e.target.value) })} className="w-full px-3 py-2 rounded-lg border border-[#EFE3D8] text-sm bg-white focus:outline-none focus:border-[#7B2C3B]" placeholder="35000" />
          </Field>
        </div>
      </section>

      <section className="soft-card rounded-2xl p-6 bg-white border border-[#EFE3D8] mb-5">
        <h3 className="font-serif text-xl text-[#2A2A2A] mb-1 inline-flex items-center gap-2">
          Style
          <InfoTip testId="info-style" title="Set the vibe">
            <p>Pick one or more style moods that describe your wedding. We use them to:</p>
            <ul className="list-disc ml-4 space-y-1">
              <li>Suggest a matching template for your public site</li>
              <li>Personalize AI-drafted invitation copy and reminders</li>
              <li>Tailor which planning articles show up first</li>
            </ul>
            <p>Pick as many as feel right. You can change them any time.</p>
          </InfoTip>
        </h3>
        <p className="text-xs text-[#6A6A6A] mb-4">Pick the moods that feel like you — used to personalize your site &amp; suggestions.</p>
        <div className="flex flex-wrap gap-2">
          {STYLE_TAGS.map((tag) => {
            const active = (profile.styleTags || []).includes(tag);
            return (
              <button
                key={tag}
                data-testid={`settings-styletag-${tag}`}
                onClick={() => toggleStyleTag(tag)}
                className={`px-3 py-1.5 rounded-full text-xs border transition-all ${active ? 'bg-[#7B2C3B] text-[#FBF7F2] border-[#7B2C3B]' : 'bg-transparent text-[#3A3A3A] border-[#EFE3D8] hover:border-[#B8896C]'}`}
                type="button"
              >
                {tag}
              </button>
            );
          })}
        </div>
      </section>

      <BillingCard toast={toast} />

      <section data-testid="settings-messaging-compliance" className="mb-5">
        <h3 className="font-serif text-xl text-[#2A2A2A] mb-3">Guest texting</h3>
        <HostSmsAuthorization />
      </section>

      <ConciergeDisclosures toast={toast} />
      <div id="concierge-script" className="mt-5"><ConciergeScriptEditor toast={toast} /></div>

      <section className="soft-card rounded-2xl p-6 bg-white border border-[#EFE3D8] mb-5">
        <h3 className="font-serif text-xl text-[#2A2A2A] mb-3">Account</h3>
        <div className="flex items-center justify-between">
          <div className="text-sm">
            <p className="text-[#2A2A2A]">{user?.email}</p>
            <p className="text-xs text-[#9A8A7E]">Signed in</p>
          </div>
          <button
            data-testid="settings-signout-btn"
            onClick={handleSignOut}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] text-[#6A6A6A] hover:text-[#7B2C3B] hover:border-[#7B2C3B]"
            type="button"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </section>

      <section className="rounded-2xl p-6 border border-dashed border-[#B8896C] bg-[#F4E4DA]/40">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-4 h-4 text-[#7B2C3B] flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-serif text-lg text-[#2A2A2A]">Redo the guided setup</h3>
            <p className="text-xs text-[#6A6A6A] mt-1 mb-3">
              Walks you through the same welcome wizard again. Your existing answers are kept — you can update, skip, or overwrite them one at a time.
            </p>
            {!confirmReset ? (
              <button
                data-testid="settings-redo-onboarding-btn"
                onClick={() => setConfirmReset(true)}
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#7B2C3B] text-[#FBF7F2] hover:bg-[#6A2530]"
                type="button"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Redo onboarding
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  data-testid="settings-redo-confirm"
                  onClick={doReset}
                  disabled={resetting}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full bg-[#7B2C3B] text-[#FBF7F2] hover:bg-[#6A2530] disabled:opacity-60"
                  type="button"
                >
                  {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                  Yes, start again
                </button>
                <button
                  data-testid="settings-redo-cancel"
                  onClick={() => setConfirmReset(false)}
                  className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] text-[#6A6A6A]"
                  type="button"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <AccountSection />
    </div>
  );
}

function AccountSection() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showPw, setShowPw] = useState(false);
  const [showDel, setShowDel] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '' });
  const [delForm, setDelForm] = useState({ password: '', confirm: '' });
  const [busy, setBusy] = useState(false);

  const changePw = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post('/account/change-password', pwForm);
      toast({ title: 'Password updated.' });
      setPwForm({ current_password: '', new_password: '' });
      setShowPw(false);
    } catch (err) {
      toast({ title: 'Could not change password', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const deleteMe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await client.post('/account/delete', delForm);
      toast({ title: 'Account deleted. We\'re sorry to see you go.' });
      await logout();
      navigate('/');
    } catch (err) {
      toast({ title: 'Could not delete account', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <section className="rounded-2xl p-6 border border-dashed border-[#B8896C] bg-[#F4E4DA]/40 mt-5" data-testid="account-danger-zone">
      <h3 className="font-serif text-lg text-[#2A2A2A] mb-1">Account</h3>
      <p className="text-xs text-[#6A6A6A] mb-4">Change your password, or permanently delete your account and all planning data.</p>

      <div className="space-y-3">
        <div>
          {!showPw ? (
            <button type="button" onClick={() => setShowPw(true)} data-testid="account-change-password-btn"
                    className="text-xs px-3 py-1.5 rounded-full border border-[#7B2C3B] text-[#7B2C3B] hover:bg-[#7B2C3B] hover:text-white">
              Change password
            </button>
          ) : (
            <form onSubmit={changePw} className="space-y-2 bg-white rounded-xl p-3 border border-[#EFE3D8]" data-testid="account-change-password-form">
              <input type="password" required placeholder="Current password" value={pwForm.current_password}
                     onChange={(e) => setPwForm({ ...pwForm, current_password: e.target.value })}
                     data-testid="account-cur-pw"
                     className="w-full px-3 py-2 rounded-xl border border-[#EFE3D8] text-sm" />
              <input type="password" required minLength={6} placeholder="New password (6+)" value={pwForm.new_password}
                     onChange={(e) => setPwForm({ ...pwForm, new_password: e.target.value })}
                     data-testid="account-new-pw"
                     className="w-full px-3 py-2 rounded-xl border border-[#EFE3D8] text-sm" />
              <div className="flex gap-2">
                <button type="submit" disabled={busy} data-testid="account-change-password-save"
                        className="text-xs px-3 py-1.5 rounded-full bg-[#7B2C3B] text-white disabled:opacity-60">
                  {busy ? 'Saving…' : 'Save'}
                </button>
                <button type="button" onClick={() => setShowPw(false)}
                        className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] text-[#6A6A6A]">Cancel</button>
              </div>
            </form>
          )}
        </div>

        <div className="pt-3 border-t border-[#EFE3D8]">
          {!showDel ? (
            <button type="button" onClick={() => setShowDel(true)} data-testid="account-delete-btn"
                    className="text-xs px-3 py-1.5 rounded-full border border-red-300 text-red-700 hover:bg-red-50">
              Delete my account
            </button>
          ) : (
            <form onSubmit={deleteMe} className="space-y-2 bg-white rounded-xl p-3 border border-red-200" data-testid="account-delete-form">
              <p className="text-xs text-red-800">This permanently erases your profile, guests, vendors, timeline, and every message. You can&apos;t undo this.</p>
              <input type="password" placeholder="Your password" value={delForm.password}
                     onChange={(e) => setDelForm({ ...delForm, password: e.target.value })}
                     data-testid="account-del-pw"
                     className="w-full px-3 py-2 rounded-xl border border-[#EFE3D8] text-sm" />
              <input required placeholder="Type DELETE to confirm" value={delForm.confirm}
                     onChange={(e) => setDelForm({ ...delForm, confirm: e.target.value })}
                     data-testid="account-del-confirm"
                     className="w-full px-3 py-2 rounded-xl border border-red-300 text-sm" />
              <div className="flex gap-2">
                <button type="submit" disabled={busy || delForm.confirm !== 'DELETE'}
                        data-testid="account-delete-confirm"
                        className="text-xs px-3 py-1.5 rounded-full bg-red-700 text-white disabled:opacity-40">
                  {busy ? 'Deleting…' : 'Delete forever'}
                </button>
                <button type="button" onClick={() => { setShowDel(false); setDelForm({ password: '', confirm: '' }); }}
                        className="text-xs px-3 py-1.5 rounded-full border border-[#EFE3D8] text-[#6A6A6A]">Cancel</button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs text-[#6A6A6A] block mb-1">{label}</label>
      {children}
    </div>
  );
}
